import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';

const MENU_QUERY_KEY = 'menu-list' as const;
const MULTIORIGIN_QUERY_KEY = 'multi-origin' as const;
const STORE_INFO_QUERY_KEY = 'store-info' as const;

export const collectionsQueryKeys = queryKeysFactory(MENU_QUERY_KEY);
export const multiOriginQueryKeys = queryKeysFactory(MULTIORIGIN_QUERY_KEY);
export const storeInfoQueryKeys = queryKeysFactory(STORE_INFO_QUERY_KEY);

export const useMenu = (
  id?: string,
  options?: Omit<
    UseQueryOptions<HttpTypes.StoreMenuResponse, FetchError, HttpTypes.StoreMenuResponse, QueryKey>,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.detail(id as string),
    queryFn: async () => sdk.site.homepage.retriveMenu(id as string),
    ...options,
  });

  return { ...data, ...rest };
};

export const useLocationMultiOrigin = (
  options?: Omit<
    UseQueryOptions<
      HttpTypes.MultiOriginLocationResponse,
      FetchError,
      HttpTypes.MultiOriginLocationResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: multiOriginQueryKeys.list(),
    queryFn: async () => sdk.site.homepage.retrieveLocationMultiOrigin(),
    ...options,
  });

  return { ...data, ...rest };
};

export const useStoreInfo = () => {
  const { data, ...rest } = useQuery({
    queryKey: storeInfoQueryKeys.list(),
    queryFn: async () => {
      const response = await sdk.site.storeInfo.get();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch store info');
    },
    staleTime: 5 * 60 * 1000,
  });

  return { data, ...rest };
};
